import {
  REQUEST_TIMEOUT_MESSAGE,
  REQUEST_TIMEOUT_STATUS_CODE,
} from 'src/constants/request.constants'
import { RequestFailureResponse } from 'src/types/plan.interface'

import { isRequestTimedOutError, RequestTimeout } from './requestCancellation'

/** The rejection every timed-out copy-flow request settles with. */
export const REQUEST_TIMEOUT_FAILURE: RequestFailureResponse = Object.freeze({
  message: REQUEST_TIMEOUT_MESSAGE,
  statusCode: REQUEST_TIMEOUT_STATUS_CODE,
})

/**
 * True when a request failed because its own deadline passed. The owner of the
 * timeout checks the controller as well as the error name, because browsers
 * that ignore abort reasons reject with a plain `AbortError`.
 */
export const hasRequestTimedOut = (
  error: unknown,
  timeout?: RequestTimeout
): boolean => Boolean(timeout?.hasElapsed()) || isRequestTimedOutError(error)

/**
 * Normalises whatever a failed request rejected with into `{ message, statusCode }`
 * for a thunk's `rejectWithValue`. The fetch client rejects a non-2xx response
 * with its parsed body (the API's own `{ statusCode, message }`), a bodiless one
 * with an `HttpError` carrying `response.status`, and an aborted request with a
 * `DOMException`.
 */
export const toRequestFailure = (
  error: unknown,
  fallbackMessage: string,
  timeout?: RequestTimeout
): RequestFailureResponse => {
  if (hasRequestTimedOut(error, timeout)) {
    return REQUEST_TIMEOUT_FAILURE
  }

  if (typeof error === 'object' && error !== null) {
    const body = error as Partial<RequestFailureResponse> & {
      response?: { status?: number }
    }
    if (typeof body.message === 'string' && body.message) {
      const statusCode =
        typeof body.statusCode === 'number'
          ? body.statusCode
          : body.response?.status
      return { message: body.message, statusCode }
    }
  }

  return { message: fallbackMessage }
}

/** True for the rejection `toRequestFailure` produces when a request timed out. */
export const isRequestTimeoutFailure = (failure: unknown): boolean =>
  (failure as Partial<RequestFailureResponse> | undefined)?.statusCode ===
  REQUEST_TIMEOUT_STATUS_CODE
